import type { UnknownAction } from '@reduxjs/toolkit'
import { App } from 'antd'
import { Effect, HashSet } from 'effect'
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import type { AgentApprovalPolicy } from '../components/prompt/approvalMode'
import { chooseTargetViewId, type OpenView } from '../domain/chatTarget'
import { isPendingId, pendingIdOf } from '../domain/pendingId'
import { isApprovalAnswerable } from '../domain/sessionChat'
import { useAgentApi } from '../gateway/agentApi'
import { answerAgentApproval } from '../gateway/answerApproval'
import { cancelTurnOrRestoreStop, sendPromptToAgent } from '../gateway/sendPrompt'
import {
  approvalAnswered,
  approvalAnswerFailed,
  approvalAnswerRejected,
  approvalAnswerSent,
  turnStarted,
  turnStopRequested,
} from '../state/agentChat'
import { targetChosen } from '../state/agentTargets'
import type { AgentChatActions } from '../state/agentWidgetContext'
import {
  agentWidgetReducer,
  type AgentWidgetState,
  chatOpened,
  initialAgentWidgetState,
  newChatStarted,
  selectCanSendToOpenChat,
  selectChatTargetViewId,
  selectIsOpenTurnStopping,
  selectOpenRunningTurnId,
  selectOpenSessionChat,
  selectOpenSessionId,
} from '../state/agentWidgetState'

/**
 * ウィジェットの状態（会話、開いているチャット、会話の対象）を持ち、指示の送信と停止を行う。
 * ウィジェットを閉じても、ページを移っても、実行中のターンの読み取りを続けるよう、閉じても描かれている AgentWidget で使う。
 * onPromptRejected は、API が受け付けなかった指示を受け取る（呼び出し側が入力欄に戻す）。断られたチャットを開いているときだけ呼ぶ。
 * approvalPolicy は入力欄の下で選んだ承認モードで、指示を送るたびに本文で渡す。
 * openView は今開いている view で、新しいチャットの最初の指示を送るときに、そのチャットの会話の対象にする
 */
export const useAgentChat = (
  organizationId: string,
  onPromptRejected: (prompt: string) => void,
  approvalPolicy: AgentApprovalPolicy,
  openView: OpenView | null,
): { state: AgentWidgetState; actions: AgentChatActions } => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const api = useAgentApi(organizationId)
  const [state, dispatchToReact] = useReducer(agentWidgetReducer, undefined, initialAgentWidgetState)
  // 操作は描き直しても同じ関数を渡す（受け取る部品は memo）ので、開いているチャットと実行中のターンは ref から読む。
  // ref は描いた後でなく dispatch した時点で進める。Effect のファイバーからの更新（セッションや turnId の付け替え）は後のタスクで描かれ、
  // その前に利用者の操作が処理されうるので、描いた後に進めると、操作が付け替え前の仮の ID に効いてしまう
  const stateReference = useRef(state)
  const dispatch = useCallback((action: UnknownAction): void => {
    stateReference.current = agentWidgetReducer(stateReference.current, action)
    dispatchToReact(action)
  }, [])
  // 開いている view も、同じ関数を渡し続けるために ref から読む。読むのは新しいチャットの最初の指示を送るときだけである
  // eslint-disable-next-line @eslint-react/naming-convention-ref-name -- unicorn/name-replacements と両立する名前がないため、「〜Reference」にする
  const openViewReference = useRef(openView)
  useEffect(() => {
    openViewReference.current = openView
  }, [openView])
  // turnId が届く前に中断を押されたターン（仮の turnId）。届いた時点で中断を送る（sendPromptToAgent）
  const cancelRequests = useRef(HashSet.empty<string>())

  const notifyCancelFailed = useCallback((): void => {
    void message.error(t(`中断できませんでした`))
  }, [message, t])

  // 指示をチャットに送る。
  // 会話の対象は、送る時点の ref から送るチャットのものを読む。新しいチャットの最初の指示で覚えた対象（targetChosen）は、まだ描いていないので ref にしかない（ADR-106）
  const startTurn = useCallback(
    (sessionKey: string, prompt: string): void => {
      const targetViewId = selectChatTargetViewId(stateReference.current, sessionKey)
      const pendingTurnId = pendingIdOf(crypto.randomUUID())
      // API の応答を待たずに、指示と「考え中」を出す
      dispatch(
        turnStarted({ agentSessionId: sessionKey, turnId: pendingTurnId, prompt, startedAt: new Date().toISOString() }),
      )

      Effect.runFork(
        sendPromptToAgent(
          {
            api,
            dispatch,
            takeCancelRequest: (turnId) => {
              const isRequested = HashSet.has(cancelRequests.current, turnId)
              cancelRequests.current = HashSet.remove(cancelRequests.current, turnId)
              return isRequested
            },
            notifyCancelFailed,
            // 応答を待つ間に新しいチャットにしていたら、入力欄はもう別のチャットのものなので、戻さない
            restorePrompt: (agentSessionId) => {
              if (selectOpenSessionId(stateReference.current) === agentSessionId) {
                onPromptRejected(prompt)
              }
            },
            messages: { sendFailed: t(`指示を送れませんでした`), disconnected: t(`応答の途中で接続が切れました`) },
          },
          { sessionKey, pendingTurnId, prompt, approvalPolicy, targetViewId },
        ),
      )
    },
    // 承認モードを変えると操作の関数も作り直される。変えるのは利用者の操作のときだけなので、受け取る部品の描き直しは増えない
    [api, approvalPolicy, dispatch, notifyCancelFailed, onPromptRejected, t],
  )

  const actions = useMemo((): AgentChatActions => {
    const openSessionIdNow = (): string | null => selectOpenSessionId(stateReference.current)

    const chatActions: AgentChatActions = {
      sendPrompt: (prompt) => {
        // 送れるかは、描いた状態でなく ref で確かめる（ADR-106）。部品の判定と同じ selector を使う
        if (!selectCanSendToOpenChat(stateReference.current)) {
          return false
        }
        const openSessionId = openSessionIdNow()
        // 新しいチャットなら、仮の ID で開いておき、セッションを作ったら作ったものに付け替える（sessionIdAssigned）
        const sessionKey = openSessionId ?? pendingIdOf(crypto.randomUUID())
        dispatch(chatOpened({ agentSessionId: sessionKey }))
        const targetViewId = chooseTargetViewId(openSessionId === null, openViewReference.current)
        if (targetViewId !== null) {
          dispatch(targetChosen({ agentSessionId: sessionKey, viewId: targetViewId }))
        }
        startTurn(sessionKey, prompt)
        return true
      },
      stopTurn: () => {
        const agentSessionId = openSessionIdNow()
        const runningTurnId = selectOpenRunningTurnId(stateReference.current)
        // 停止中に重ねて押されても、中断を重ねて送らない
        if (agentSessionId === null || runningTurnId === null || selectIsOpenTurnStopping(stateReference.current)) {
          return
        }
        dispatch(turnStopRequested({ agentSessionId, turnId: runningTurnId }))
        // turnId が届く前（セッションを作っている間、指示の応答を待っている間）は、要求を覚えておき、届いた時点で送る
        if (isPendingId(runningTurnId)) {
          cancelRequests.current = HashSet.add(cancelRequests.current, runningTurnId)
          return
        }
        Effect.runFork(
          cancelTurnOrRestoreStop({ api, dispatch, notifyCancelFailed }, { agentSessionId, turnId: runningTurnId }),
        )
      },
      // 前のチャットの実行中のターンは止めない。読み取りは続く
      startNewChat: () => {
        dispatch(newChatStarted())
      },
      // 承認のカードは開いているチャットの会話にだけ出るので、開いているチャットのターンへの回答として送る。
      // 送っている間と答えた後は送らない。runtime は最初の答えだけを受け付けるので、後から送った答えは効かない
      answerApproval: (turnId, approvalId, decision) => {
        const agentSessionId = openSessionIdNow()
        if (
          agentSessionId === null ||
          !isApprovalAnswerable(selectOpenSessionChat(stateReference.current), approvalId)
        ) {
          return
        }
        dispatch(approvalAnswerSent({ agentSessionId, approvalId }))
        Effect.runFork(
          answerAgentApproval(api, { agentSessionId, turnId, approvalId }, decision).pipe(
            Effect.match({
              onSuccess: (result) => {
                if (result.accepted) {
                  dispatch(approvalAnswered({ agentSessionId, approvalId, answer: decision }))
                } else {
                  // 期限切れか回答済み（notPending）、runtime がそのターンを実行していない（notRunning）、知らない理由（unknown。ADR-006）。
                  // どれも送り直しても受け付けられないので、カードに受け付けられなかったことを出し、ボタンを消す
                  dispatch(approvalAnswerRejected({ agentSessionId, approvalId }))
                }
              },
              // 送れなかったときは、もう一度押せるように戻す
              onFailure: () => {
                dispatch(approvalAnswerFailed({ agentSessionId, approvalId }))
                void message.error(t(`承認を送れませんでした`))
              },
            }),
          ),
        )
      },
    }
    return chatActions
  }, [api, dispatch, message, notifyCancelFailed, startTurn, t])

  return { state, actions }
}
